/**
 * Gerador de numeros pseudoaleatorios semeado e reproduzivel.
 *
 * Algoritmo: cyrb128 (string -> estado de 128 bits) + sfc32 (gerador).
 *
 * Por que nao `Math.random`: nao e semeavel. Por que nao uma biblioteca: precisamos
 * *possuir* o algoritmo, porque a reprodutibilidade do banco de itens depende dele
 * nunca mudar. Sao 40 linhas.
 *
 * O passo do sfc32 usa somente `+`, `<<`, `>>>` e `| 0` -- operacoes inteiras, sem
 * ponto flutuante no estado. Isso garante fluxos bit-identicos em qualquer motor JS
 * (V8, JSC, SpiderMonkey) e em qualquer versao de Node, hoje e daqui a dez anos.
 * Periodo ~2^128.
 */

/** Semente como tipo nominal, para nao trocar acidentalmente com uma string qualquer. */
export type Seed = string & { readonly __seed: unique symbol };

export const seed = (s: string): Seed => s as Seed;

export interface Rng {
  /** Uniforme em [0, 1), 32 bits de entropia. */
  next(): number;
  /** Inteiro uniforme em [lo, hi], inclusive nos dois extremos. */
  int(lo: number, hi: number): number;
  /** Real uniforme em [lo, hi). */
  float(lo: number, hi: number): number;
  /** Escolhe um elemento. Lanca se o array estiver vazio. */
  pick<T>(xs: readonly T[]): T;
  /** Fisher-Yates; devolve um array novo, nao muta a entrada. */
  shuffle<T>(xs: readonly T[]): T[];
  /** `true` com probabilidade p. */
  bool(p?: number): boolean;
  /**
   * Sub-fluxo independente e rotulado.
   *
   * ESTA E A DECISAO MAIS IMPORTANTE DO DESIGN. Com um fluxo linear unico,
   * acrescentar um `next()` no codigo da figura desloca todos os numeros seguintes
   * e muda silenciosamente 100% do banco de itens. Com sub-fluxos rotulados,
   * `fork('figura')` e `fork('alternativas')` sao mutuamente imunes.
   */
  fork(label: string): Rng;
  /** Caminho de rotulos ate aqui; util em mensagens de erro. */
  readonly path: string;
}

/** Hash de string para 4 palavras de 32 bits. Determinista e bem distribuido. */
function cyrb128(s: string): [number, number, number, number] {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < s.length; i++) {
    const k = s.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

/** sfc32: rapido, estado de 128 bits, passa PractRand. */
function sfc32(a: number, b: number, c: number, d: number): () => number {
  return function nextValue(): number {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

class SeededRng implements Rng {
  readonly #next: () => number;
  readonly path: string;

  constructor(material: string) {
    const [a, b, c, d] = cyrb128(material);
    this.#next = sfc32(a, b, c, d);
    this.path = material;
    // Descarta os primeiros valores: o sfc32 precisa de algumas iteracoes para
    // dissipar a correlacao com o estado inicial derivado do hash.
    for (let i = 0; i < 12; i++) this.#next();
  }

  next(): number {
    return this.#next();
  }

  int(lo: number, hi: number): number {
    if (!Number.isInteger(lo) || !Number.isInteger(hi)) {
      throw new RangeError(`int() exige inteiros; recebeu [${lo}, ${hi}]`);
    }
    if (hi < lo) throw new RangeError(`int() com intervalo vazio: [${lo}, ${hi}]`);
    return lo + Math.floor(this.next() * (hi - lo + 1));
  }

  float(lo: number, hi: number): number {
    return lo + this.next() * (hi - lo);
  }

  pick<T>(xs: readonly T[]): T {
    if (xs.length === 0) throw new RangeError('pick() de array vazio');
    // `noUncheckedIndexedAccess` obriga a checagem; o guard acima ja garante.
    return xs[this.int(0, xs.length - 1)] as T;
  }

  shuffle<T>(xs: readonly T[]): T[] {
    const out = [...xs];
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const tmp = out[i] as T;
      out[i] = out[j] as T;
      out[j] = tmp;
    }
    return out;
  }

  bool(p = 0.5): boolean {
    return this.next() < p;
  }

  fork(label: string): Rng {
    return new SeededRng(`${this.path}/${label}`);
  }
}

/**
 * Constroi o RNG-raiz de uma instancia de item.
 *
 * A VERSAO DO TEMPLATE ENTRA NA SEMENTE de proposito. Sem isso, corrigir um typo no
 * gerador faria uma seed antiga produzir um item diferente daquele em que o aluno foi
 * pontuado -- e todo parametro TRI calibrado passaria a se referir a um item que nao
 * existe mais. Subir `version` invalida deliberadamente as variantes em cache.
 */
export function rngForTemplate(templateId: string, version: number, s: Seed): Rng {
  return new SeededRng(`${templateId}@${version}#${s}`);
}

/** RNG avulso, para testes e utilitarios. */
export function rngFromString(material: string): Rng {
  return new SeededRng(material);
}

/** Sequencia deterministica de seeds, usada para varrer o espaco de variantes. */
export function* seedSequence(prefix = 's', from = 0, to = Infinity): Generator<Seed> {
  for (let i = from; i < to; i++) yield seed(`${prefix}${i}`);
}
